<template>
    <div>
        <FullscreenLoading v-if="isLoading" />

        <div class="row mt-3">
            <div class="col-12">
                <div class="card">
                    <div class="card-header pb-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <span class="h4">{{ $t('welcome_sign_overrides.title') }}</span>
                            </div>

                            <div class="text-right">
                                <div class="actions mr-n2">
                                    <div class="float-right" @click="showWelcomeSignOverrideModal = true" style="cursor: pointer;">
                                        <button class="btn btn-secondary btn-sm rounded-circle btn-icon-only float-right">
                                            <icon name="plus"></icon>
                                        </button>
                                        <h2 class="h6 mt-2 mr-2 float-right">{{ $t('welcome_sign_overrides.new_welcome_sign_override') }}</h2>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="card-body">
                        <table class="table table-sm table-no-side-paddings table-striped" style="table-layout: fixed">
                            <thead>
                                <tr>
                                    <th style="width: 190px">{{ $t('welcome_sign_overrides.image') }}</th>
                                    <Ordering @change="loadWelcomeSignOverrides()" v-model="order" field="text" tag="th">
                                        {{ $t('welcome_sign_overrides.text') }}
                                    </Ordering>
                                    <Ordering @change="loadWelcomeSignOverrides()" v-model="order" field="start" tag="th">
                                        {{ $t('welcome_sign_overrides.start') }}
                                    </Ordering>
                                    <Ordering @change="loadWelcomeSignOverrides()" v-model="order" field="end" tag="th">
                                        {{ $t('welcome_sign_overrides.end') }}
                                    </Ordering>
                                    <th v-if="store.state.companySettings.welcome_sign_music_enabled">{{ $t('welcome_sign_overrides.music') }}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td />

                                    <td>
                                        <input
                                            :placeholder="$t('welcome_sign_overrides.text')"
                                            @change="loadWelcomeSignOverrides(1)"
                                            class="form-control form-control-filtering"
                                            type="text"
                                            v-model="filtering.text"
                                        />
                                    </td>

                                    <td>
                                        <datetime-picker
                                            v-model="filtering.start"
                                            :placeholder="$t('welcome_sign_overrides.start')"
                                            type="datetime"
                                            inputClass="form-control form-control-filtering" class="theme-company"
                                            :format="'dd-MM-yyyy'"
                                            @input="loadWelcomeSignOverrides(1)"
                                        />
                                    </td>

                                    <td>
                                        <datetime-picker
                                            v-model="filtering.end"
                                            :placeholder="$t('welcome_sign_overrides.end')"
                                            type="datetime"
                                            inputClass="form-control form-control-filtering" class="theme-company"
                                            :format="'dd-MM-yyyy'"
                                            @input="loadWelcomeSignOverrides(1)"
                                        />
                                    </td>

                                    <td v-if="store.state.companySettings.welcome_sign_music_enabled" />
                                    <td />
                                </tr>

                                <tr v-for="welcomeSignOverride of welcomeSignOverrides.data" :key="welcomeSignOverride.id">
                                    <td>
                                        <a v-if="welcomeSignOverride.image_url" :href="welcomeSignOverride.image_url" target="_blank">
                                            <img
                                                :key="welcomeSignOverride.id + '_' + welcomeSignOverride.updated_at"
                                                :src="welcomeSignOverride.image_url"
                                                :style="getPreviewStyle(welcomeSignOverride.welcome_sign)"
                                            />
                                        </a>
                                        <span v-else>{{ $t('welcome_sign_overrides.no_preview_available') }}</span>
                                    </td>

                                    <td>{{ welcomeSignOverride.text }}</td>
                                    <td>{{ formatDateTime(welcomeSignOverride.start) }}</td>
                                    <td>{{ formatDateTime(welcomeSignOverride.end) }}</td>

                                    <td v-if="store.state.companySettings.welcome_sign_music_enabled">
                                        <div v-if="welcomeSignOverride.songs?.length">
                                            <div v-for="(song, index) of welcomeSignOverride.songs" :key="index" class="mb-4">
                                                <div class="d-flex align-items-center mb-1">
                                                    <img
                                                        style="width: 25px; height: 25px;"
                                                        :src="song.song.cover ? song.song.cover_public_path : '/images/audio.png'"
                                                        class="avatar rounded-circle mr-2"
                                                    />
                                                    <span>{{ song.song.display_name }} - {{ song.song.author }}</span>
                                                </div>
                                                <SongPlayer
                                                    :preload="'none'"
                                                    :src="song.song.public_path"
                                                    :key="'song_' + song.song.id"
                                                />
                                            </div>
                                        </div>
                                        <div v-else>
                                            -
                                        </div>
                                    </td>
                                    <td class="text-right">
                                        <button
                                            class="btn btn-xs rounded-pill btn-danger"
                                            @click="welcomeSignOverrideToDelete = welcomeSignOverride; showWelcomeSignOverrideDeleteModal = true"
                                        >
                                            <icon name="trash" scale="0.8"></icon>
                                        </button>

                                        <button
                                            class="btn btn-xs rounded-pill btn-primary"
                                            @click="welcomeSignOverrideToEdit = welcomeSignOverride; showWelcomeSignOverrideModal = true"
                                        >
                                            <icon name="pencil-alt" scale="0.8" />
                                        </button>
                                    </td>
                                </tr>

                                <tr v-if="welcomeSignOverrides.data.length === 0 && !isLoading">
                                    <td colspan="6" class="no-results">
                                        {{ $t('welcome_sign_overrides.no_welcome_sign_overrides_found') }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="card-footer border-0">
                        <Pagination :pagination="welcomeSignOverrides" @change="loadWelcomeSignOverrides" />
                    </div>
                </div>
            </div>
        </div>

        <WelcomeSignOverrideModal
            v-model="showWelcomeSignOverrideModal"
            :form-errors="formErrors"
            :is-loading="isLoading"
            :welcome-sign-override="welcomeSignOverrideToEdit"
            @save="saveWelcomeSignOverride"
            @close="welcomeSignOverrideToEdit = null"
        />

        <DeleteWelcomeSignOverrideModal
            v-model="showWelcomeSignOverrideDeleteModal"
            :is-loading="isLoading"
            :welcome-sign-override="welcomeSignOverrideToDelete"
            @delete="deleteWelcomeSignOverride"
        />
    </div>
</template>
<script setup>
    import Pagination from '../../components/Pagination.vue';
    import FullscreenLoading from '../../components/FullscreenLoading.vue';
    import Ordering from '../../components/Ordering.vue';
    import { inject, onMounted, ref } from 'vue';
    import filteringFromObject from '../../utils/filteringFromObject.js';
    import Axios from 'axios';
    import { notifyError, notifySuccess } from '../../utils/notify.js';
    import SongPlayer from '../../components/SongPlayer.vue';
    import i18n from '../../utils/locale.js';
    import DatetimePicker from '../../components/DatetimePicker.vue';
    import { formatDateTime } from '../../utils/formatDate.js';
    import { getPreviewStyle } from '../../utils/welcomeSign.js';
    import WelcomeSignOverrideModal from './modals/WelcomeSignOverrideModal.vue';
    import DeleteWelcomeSignOverrideModal from './modals/DeleteWelcomeSignOverrideModal.vue';

    const store = inject('store');

    const isLoading = ref(false);

    const order = ref('id,desc');
    const filtering = ref({});

    const welcomeSignOverrides = ref({
        data: [],
        current_page: 1,
    });

    const welcomeSignOverrideToEdit = ref(null);
    const welcomeSignOverrideToDelete = ref(null);

    const showWelcomeSignOverrideModal = ref(false);
    const showWelcomeSignOverrideDeleteModal = ref(false);

    const formErrors = ref(null);

    onMounted(() => {
        loadWelcomeSignOverrides();
    });

    async function loadWelcomeSignOverrides(page = undefined) {
        isLoading.value = true;

        try {
            const response = await Axios.get('/api/welcome-sign-overrides', {
                params: {
                    page: page || welcomeSignOverrides.value.current_page,
                    order: order.value,
                    filters: filteringFromObject(filtering.value, {
                        start: 'date',
                        end: 'date',
                    }),
                },
            });

            welcomeSignOverrides.value = response.data;
        } catch (exception) {
            notifyError(exception);
        }

        isLoading.value = false;
    }

    async function saveWelcomeSignOverride(data) {
        formErrors.value = null;
        isLoading.value = true;

        try {
            const formData = {
                ...data,
                start: data.start ? formatDateTime(data.start, 'DD-MM-YYYY HH:mm:ss') : null,
                end: data.end ? formatDateTime(data.end, 'DD-MM-YYYY HH:mm:ss') : null,
            };

            if (welcomeSignOverrideToEdit.value) {
                await Axios.put('/api/welcome-sign-overrides/' + welcomeSignOverrideToEdit.value.id, formData);
            } else {
                await Axios.post('/api/welcome-sign-overrides', formData);
            }

            await loadWelcomeSignOverrides();

            welcomeSignOverrideToEdit.value = null;
            showWelcomeSignOverrideModal.value = false;

            notifySuccess(i18n.t('welcome_sign_overrides.welcome_sign_override_saved'));
        } catch (exception) {
            formErrors.value = exception?.response?.data?.errors || null;

            notifyError(exception);
        }

        isLoading.value = false;
    }

    async function deleteWelcomeSignOverride(welcomeSignOverride) {
        isLoading.value = true;

        try {
            await Axios.delete('/api/welcome-sign-overrides/' + welcomeSignOverride.id);
            await loadWelcomeSignOverrides();

            welcomeSignOverrideToDelete.value = null;
            showWelcomeSignOverrideDeleteModal.value = false;

            notifySuccess(i18n.t('welcome_signs.override_deleted'));
        } catch (exception) {
            notifyError(exception);
        }

        isLoading.value = false;
    }
</script>
